import type { Channel } from "@expert/shared-types";

import { createSimpleStore } from "@expert/state-utils";

import type { AgentActivity } from "../types";

/** Provider-agnostic queue information. */
export interface AgentQueueInfo {
    id: string;
    name: string;
}

/** Provider-agnostic channel capacity: concurrent tasks per channel (0 = not supported). */
export type ChannelCapacity = Partial<Record<Channel, number>>;

interface AgentState {
    agentId: string;
    agentName: string;
    agentTools: string[];
    activity: AgentActivity | undefined;
    activityUpdatedAt?: number;
    pendingActivity: AgentActivity | undefined;

    /** Channel capacity derived from the agent's routing profile. */
    channelCapacity: ChannelCapacity;

    /** Queues the agent is assigned to (provider-agnostic). */
    assignedQueues: AgentQueueInfo[];
}

interface AgentActions {
    setAgentTools: (tools: string[]) => void;
    setAgentActivity: (activity: AgentActivity, activityUpdatedAt?: number) => void;
    setPendingActivity: (activity: AgentActivity | undefined) => void;
    initAgent: (
        id: string,
        name: string,
        activity: AgentActivity | undefined,
        activityUpdatedAt: number,
        pendingActivity?: AgentActivity,
    ) => void;

    /** Update channel capacity and (optionally) assigned queues. Called by the CCaaS provider SDK. */
    setChannelConfig: (channelCapacity: ChannelCapacity, assignedQueues?: AgentQueueInfo[]) => void;

    /** Whether the agent's routing profile supports a specific channel. */
    supportsChannel: (channel: Channel) => boolean;

    /** Capacity for a specific channel (0 if not supported). */
    getChannelCapacity: (channel: Channel) => number;
}

export type AgentStore = AgentState & AgentActions;

export const useAgentStore = createSimpleStore<AgentStore>({
    devtools: {
        store: "agent",
        name: "workspace/sdk",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set, get) => ({
    // Initial state values
    agentId: "---", // fallback value
    agentName: "Agent", // fallback value
    agentTools: [],
    activity: undefined,
    pendingActivity: undefined,
    activityUpdatedAt: undefined,
    channelCapacity: {},
    assignedQueues: [],

    // Actions
    setAgentActivity: (activity: AgentActivity, activityUpdatedAt?: number) => {
        set(
            {
                activity,
                ...(activityUpdatedAt != null && { activityUpdatedAt }),
            },
            false,
            "setAgentActivity",
        );
    },

    setPendingActivity: (activity?: AgentActivity) => {
        set(
            {
                pendingActivity: activity,
            },
            false,
            "setPendingActivity",
        );
    },

    setAgentTools: (tools: string[]) => {
        set(
            {
                agentTools: tools,
            },
            false,
            "setAgentTools",
        );
    },

    setChannelConfig: (channelCapacity: ChannelCapacity, assignedQueues?: AgentQueueInfo[]) => {
        set(
            {
                channelCapacity,
                ...(assignedQueues !== undefined && { assignedQueues }),
            },
            false,
            "setChannelConfig",
        );
    },

    supportsChannel: (channel: Channel) => {
        const capacity = get().channelCapacity[channel];
        return capacity !== undefined && capacity > 0;
    },

    getChannelCapacity: (channel: Channel) => get().channelCapacity[channel] ?? 0,

    initAgent: (
        id: string,
        name: string,
        activity: AgentActivity | undefined,
        activityUpdatedAt: number,
        pendingActivity?: AgentActivity,
    ) => {
        set(
            {
                agentId: id,
                agentName: name,
                activity,
                activityUpdatedAt,
                pendingActivity,
            },
            false,
            "initAgent",
        );
    },
}));
